import type { Ssm5FreezeFramesDocumentFormat } from '@packages/domain/ssm5';
import type { TextInput } from 'react-native';

import { colors } from '@packages/core/styles';
import { Button, Icon, TextInputHelper } from '@packages/ui/shared';
import { DropdownButton } from '@web/components/dropdown-button';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';

/** Props for {@link Ssm5FreezeFramesToolbar}. */
export type Ssm5FreezeFramesToolbarProps = {
    /** Whether at least one PID can be downloaded. */
    isDownloadEnabled: boolean;
    /** Receives the selected document format. */
    onDownload: (format: Ssm5FreezeFramesDocumentFormat) => void;
    /** Receives every change to the search text. */
    onQueryChange: (query: string) => void;
    /** The current search text. */
    query: string;
};

/** The document formats in menu order. */
const FORMATS: readonly Ssm5FreezeFramesDocumentFormat[] = ['pdf', 'csv'];

/** The size of the toolbar icons. */
const ICON_SIZE = 24;

const useStyles = createUseStyles({
    /** The app-wide form theme colors every placeholder with the same weight, so the design's color repeats it. */
    search: {
        '& input::placeholder': { color: `${colors.textDarkSecondary} !important` },
    },
    toolbar: {
        alignItems: 'center',
        display: 'flex',
        flexShrink: 0,
        height: 72,
        justifyContent: 'space-between',
        padding: '16px 24px',
    },
});

/**
 * The PID search and the document download menu.
 *
 * @param props - {@link Ssm5FreezeFramesToolbarProps}
 * @returns the toolbar
 */
export function Ssm5FreezeFramesToolbar({
    isDownloadEnabled,
    onDownload,
    onQueryChange,
    query,
}: Ssm5FreezeFramesToolbarProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const searchInput = useRef<TextInput>(null);

    const isQueryEmpty = query === '';
    const items = FORMATS.map((format) => ({ key: format, label: t(`ssm5:ffd.download.${format}`) }));
    const clearQuery = useCallback(() => {
        onQueryChange('');
        searchInput.current?.focus();
    }, [onQueryChange]);

    return (
        <div className={classes.toolbar}>
            <div className={classes.search}>
                <TextInputHelper
                    accessibilityLabel={t('ssm5:ffd.search.placeholder')}
                    inputContainerStyle={ssm5ControlStyles.freezeFramesSearch}
                    onChangeText={onQueryChange}
                    placeholder={t('ssm5:ffd.search.placeholder')}
                    placeholderTextColor={colors.textDarkSecondary}
                    ref={searchInput}
                    rightIcon={
                        isQueryEmpty ? (
                            <Icon color="grayFive" name="search" size={ICON_SIZE} />
                        ) : (
                            <Button
                                accessibilityLabel={t('ssm5:ffd.search.clear')}
                                onPress={clearQuery}
                                style={ssm5ControlStyles.freezeFramesClear}
                                variant="ghost-gray"
                            >
                                <Icon color="textDarkSecondary" name="close" size={ICON_SIZE} />
                            </Button>
                        )
                    }
                    textInputStyle={ssm5TextStyles.fieldText}
                    value={query}
                />
            </div>
            <DropdownButton
                icon="sync-down"
                id="ssm5-freeze-frames-download"
                isDisabled={!isDownloadEnabled}
                isOpen={isMenuOpen}
                items={items}
                onSelect={onDownload}
                onToggle={setIsMenuOpen}
                title={t('ssm5:ffd.download.button')}
            />
        </div>
    );
}
